import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

export interface RefetchClinicalAttachmentPayload {
  identifier: string;
}

export interface RefetchClinicalAttachmentResponse {
  success: boolean;
  message?: string;
  data?: any;
}

/**
 * Makes a POST call to refetch clinical attachment for a given identifier
 * @param payload - The refetch payload containing identifier
 * @returns Promise<RefetchClinicalAttachmentResponse>
 */
export const refetchClinicalAttachment = async (
  payload: RefetchClinicalAttachmentPayload,
): Promise<RefetchClinicalAttachmentResponse> => {
  try {
    const token = await getBearerToken();

    if (!token) {
      throw new Error("Bearer token not available");
    }

    // Replace the placeholder in the endpoint with actual identifier
    const endpoint = API_ENDPOINTS.REFETCH_CLINICAL_ATTACHMENT.replace(
      "Identifier",
      payload.identifier,
    );

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        accept: "application/json",
      },
      body: "",
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError(
        new Error(
          `Refetch clinical attachment failed: ${response.status} ${response.statusText}`,
        ),
        errorText,
      );
      return {
        success: false,
        message: `Request failed with status ${response.status}: ${response.statusText}`,
      };
    }

    const responseData = await response.json();

    return {
      success: true,
      data: responseData,
      message: "Clinical attachment refetch initiated successfully",
    };
  } catch (error) {
    logError(error as Error, "Error in refetchClinicalAttachment");
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};
